<template>
  <div>
    <v-progress-linear fixed color="primary" indeterminate v-if="loading"></v-progress-linear>
    <v-container>
      <v-row>
        <v-col>
          <h2 class="mt-3">Checkliste ausfüllen</h2>
          <h3 class="mb-0" v-if="checklist">{{ checklist.identifier }}</h3>
        </v-col>
      </v-row>

      <v-form v-if="checklist" ref="form" style="width: 100%">
        <v-row v-for="(checklistItem, index) in items" :key="index">
          <v-col cols="12">
            <v-card class="my-4" elevation="2" v-if="checklistItem">
              <!-- Item Details Section (Title, Body, Video) - No Changes -->
              <v-row no-gutters>
                <v-col cols="12">
                  <v-card-title class="d-flex justify-space-between">
                    <h4>{{ checklistItem.title }}</h4>
                    <v-chip color="primary" outlined>
                      Anzahl: <strong>{{ checklistItem.anzahl }}</strong>
                    </v-chip>
                  </v-card-title>
                  <v-card-text>
                    <div class="item-body" v-html="checklistItem.body"></div>
                    <div v-if="checklistItem.video_path" class="mt-4">
                      <v-btn icon fab elevation="2" @click="showVideo(checklistItem.video_path)">
                        <v-icon>mdi-video</v-icon>
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-col>
              </v-row>

              <v-divider></v-divider>

              <!-- ================================================================= -->
              <!-- NEW MOBILE-FRIENDLY INPUT SECTION                               -->
              <!-- ================================================================= -->
              <v-card-actions class="pa-0">
                <v-container fluid>
                  <v-row>
                    <!-- "OK" Button -->
                    <v-col cols="6" class="pa-1">
                      <v-btn
                          block large
                          :outlined="items[index].is_ok !== true"
                          color="success"
                          @click="setStatus(index, true)"
                          :disabled="readOnly"
                      >
                        <v-icon left>mdi-check-circle-outline</v-icon>
                        OK
                      </v-btn>
                    </v-col>

                    <!-- "Problem" Button -->
                    <v-col cols="6" class="pa-1">
                      <v-btn
                          block large
                          :outlined="items[index].is_ok !== false"
                          color="error"
                          @click="setStatus(index, false)"
                          :disabled="readOnly"
                      >
                        <v-icon left>mdi-alert-circle-outline</v-icon>
                        Problem
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Conditional Comment Field -->
                  <v-row v-if="items[index].is_ok === false">
                    <v-col class="pt-2">
                      <v-textarea
                          v-model="items[index].remark"
                          label="Kommentar zum Problem"
                          :rules="textRules"
                          :disabled="readOnly"
                          ref="remarkField"
                          rows="3"
                          auto-grow
                          outlined
                          required
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <!-- Hidden radio group for Vuetify validation -->
                  <v-radio-group v-model="items[index].is_ok" :rules="[v => v !== null || 'Bitte Status auswählen']" class="d-none">
                    <v-radio :value="true"></v-radio>
                    <v-radio :value="false"></v-radio>
                  </v-radio-group>

                </v-container>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </v-form>

      <v-row>
        <v-col cols="12" class="text-right">
          <div v-if="isNewList">
            <v-btn fab class="mx-3" title="Zurück" @click="$router.go(-1)"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <v-btn fab :loading="loading" color="primary" title="Checkliste speichern" @click="createChecklist"><v-icon>mdi-content-save</v-icon></v-btn>
          </div>
          <div v-else>
            <v-btn fab class="mx-3" title="Zurück" @click="$router.go(-1)"><v-icon>mdi-arrow-left</v-icon></v-btn>
            <v-btn v-if="!readOnly" :loading="loading" color="primary" fab class="mx-3" title="Änderungen speichern" @click="saveChecklist(false)"><v-icon>mdi-content-save</v-icon></v-btn>
            <v-btn v-if="!readOnly && review !== 'true'" :loading="loading" color="success" fab class="mx-3" title="Checkliste abschliessen" @click="validateList"><v-icon>mdi-send-check</v-icon></v-btn>
          </div>
        </v-col>
      </v-row>

      <!-- ================================================================= -->
      <!-- DIALOGS (COMPLETE)                                              -->
      <!-- ================================================================= -->
      <v-dialog v-model="dialog.checklistFinished" persistent max-width="600">
        <v-card>
          <v-card-title class="headline">Checkliste geschlossen</v-card-title>
          <v-card-text>
            <p>Die Checkliste wurde erfolgreich geschlossen und zur Prüfung gegeben.</p>
            <div v-if="nextList" class="mt-4">Nächste offene Checkliste: <strong>{{ nextList.title }}</strong></div>
            <div v-else class="mt-4">Dies war die letzte offene Checkliste für dieses Fahrzeug.</div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" text @click="$router.push({name: 'Home'})">Zur Startseite</v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="nextList" :loading="loading" text color="success" @click="openNewList()">Nächste Checkliste öffnen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialog.confirmComplete" persistent max-width="400">
        <v-card>
          <v-card-title class="headline">Checkliste abschliessen?</v-card-title>
          <v-card-text>Die Checkliste wird als ausgefüllt markiert und kann danach nicht mehr bearbeitet werden. Wollen Sie fortfahren?</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog.confirmComplete = false">Abbrechen</v-btn>
            <v-btn color="primary" text :loading="loading" @click="saveChecklist(true)">Weiter</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="dialog.video" max-width="800px">
        <v-card>
          <v-card-title>Video</v-card-title>
          <v-card-text>
            <video v-if="dialog.video" controls :src="videoSrc" style="width:100%" autoplay>
              Ihr Browser unterstützt das Video-Tag nicht.
            </video>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="dialog.video = false">Schliessen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snack.state" :timeout="snack.timeout" :color="snack.color" top right>{{ snack.message }}</v-snackbar>
    </v-container>
  </div>
</template>

<script>
// The script from the previous answer is correct and complete.
// No changes are needed here.
import axios from "axios";

export default {
  name: "PopulateChecklist",
  props: ['id', 'newList', 'readOnly', 'review'],
  data() {
    return {
      checklist: null,
      nextList: null,
      items: [],
      videoSrc: '',
      showRemark: [],
      dialog: { confirmComplete: false, video: false, checklistFinished: false },
      loading: true,
      textRules: [v => !!v || 'Muss ausgefüllt sein'],
      snack: { state: false, color: 'success', timeout: 3000, message: '' }
    }
  },
  computed: {
    isNewList() {
      return this.$route.query.newList !== 'false' && this.newList !== 'false';
    }
  },
  created() {
    if (this.isNewList) {
      this.loadNewChecklist();
    } else {
      this.loadExistingList();
    }
  },
  methods: {
    setStatus(index, isOk) {
      // Set the status for the item
      this.$set(this.items, index, { ...this.items[index], is_ok: isOk });

      // If "Problem" was tapped, focus the comment field
      if (isOk === false) {
        // Use $nextTick to wait for the DOM to update and the field to become visible
        this.$nextTick(() => {
          if (this.$refs.remarkField && this.$refs.remarkField[0]) {
            this.$refs.remarkField[0].focus();
          }
        });
      }
    },
    getChecklists() {
      // Guard Clause: Make sure we have the necessary data before proceeding.
      if (!this.checklist || !this.checklist.vehicle) {
        console.error("Cannot get next checklist: vehicle information is missing.");
        return;
      }

      // This logic correctly extracts the ID whether 'vehicle' is populated or not.
      const vehicleId = typeof this.checklist.vehicle === 'object'
          ? this.checklist.vehicle._id
          : this.checklist.vehicle;

      axios.get(`/checklists/by_vehicle/${vehicleId}`)
          .then((response) => {
            // The logic here remains the same as it correctly uses the 'openLists' array
            const openLists = response.data.openLists.filter(el => el.identifier !== this.checklist.identifier);
            this.nextList = openLists.length > 0 ? openLists[0] : null;
          })
          .catch(() => this.showAlert('Nächste Checkliste konnte nicht geladen werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    loadNewChecklist() {
      this.loading = true;
      axios.get(`/checklists/${this.id}`)
          .then((response) => {
            this.checklist = response.data;
            this.items = this.checklist.items.map(checklistItem => {
              if (!checklistItem || !checklistItem.item) return null;
              return {
                is_ok: null,
                remark: '',
                item: checklistItem.item._id,
                title: checklistItem.item.title,
                body: checklistItem.item.body,
                anzahl: checklistItem.anzahl,
                video_path: checklistItem.item.video_path,
              };
            }).filter(Boolean);
            this.showRemark = this.items.map(() => true);
          })
          .catch(() => this.showAlert('Serverfehler. Daten konnten nicht geladen werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    loadExistingList(listId = null) {
      this.loading = true;
      const targetId = listId || this.id;
      axios.get(`/pchecklists/${targetId}`)
          .then((response) => {
            this.checklist = response.data;
            this.items = this.checklist.items.map(pChecklistItem => {
              if (!pChecklistItem || !pChecklistItem.item) return null;
              return {
                _id: pChecklistItem._id,
                is_ok: pChecklistItem.is_ok,
                remark: pChecklistItem.remark,
                item: pChecklistItem.item._id,
                title: pChecklistItem.item.title,
                body: pChecklistItem.item.body,
                anzahl: pChecklistItem.anzahl,
                video_path: pChecklistItem.item.video_path,
              };
            }).filter(Boolean);
            this.showRemark = this.items.map(item => item.is_ok !== false);
          })
          .catch(() => this.showAlert('Serverfehler. Daten konnten nicht geladen werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    setShowRemark(index, val) {
      this.$set(this.showRemark, index, val);
    },

    createChecklist() {
      if (!this.$refs.form.validate()) {
        return this.showAlert('Bitte alle Felder ausfüllen.', 'warning');
      }
      this.loading = true;
      axios.post(`/pchecklists/${this.id}`, { items: this.items })
          .then((response) => {
            this.showAlert('Checkliste wurde erfolgreich gespeichert!');
            this.$router.replace({
              name: 'PopulateChecklist',
              params: { id: response.data._id },
              query: { newList: 'false' }
            }).catch(()=>{});
            this.loadExistingList(response.data._id);
          })
          .catch(() => this.showAlert('Serverfehler. Checkliste konnte nicht erstellt werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    validateList() {
      if (this.$refs.form.validate()) {
        this.dialog.confirmComplete = true;
      } else {
        this.showAlert('Checkliste kann nicht abgeschlossen werden. Es sind nicht alle Felder ausgefüllt!', 'red');
      }
    },

    saveChecklist(close = false) {
      if (!this.$refs.form.validate()) {
        return this.showAlert('Bitte alle Felder ausfüllen, um zu speichern.', 'warning');
      }
      this.loading = true;
      axios.patch(`/pchecklists/${this.id}`, { items: this.items })
          .then(() => {
            if (close) {
              this.closeChecklist();
            } else {
              this.loadExistingList();
              this.showAlert('Checkliste wurde erfolgreich gespeichert!');
            }
          })
          .catch(() => this.showAlert('Serverfehler. Checkliste konnte nicht gespeichert werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    closeChecklist() {
      this.loading = true;
      axios.patch(`/pchecklists/${this.id}`, { state: 'review' })
          .then(() => {
            this.getChecklists();
            this.dialog.confirmComplete = false;
            this.dialog.checklistFinished = true;
          })
          .catch(() => this.showAlert('Serverfehler. Checkliste konnte nicht geschlossen werden.', 'error'))
          .finally(() => { this.loading = false; });
    },

    openNewList() {
      if (this.nextList) {
        this.$router.push({ name: 'PopulateChecklist', params: { id: this.nextList._id }, query: { newList: 'true' }})
            .then(() => {
              window.location.reload();
            });
      } else {
        this.showAlert('Es wurde keine nächste Checkliste gefunden!', 'warning');
      }
    },

    showVideo(path) {
      this.videoSrc = process.env.VUE_APP_STATIC_URL + '/uploads/' + path;
      this.dialog.video = true;
    },

    showAlert(message, color = 'success', timeout = 3000) {
      this.snack.message = message;
      this.snack.color = color;
      this.snack.state = true;
    }
  }
}
</script>

<style>
.item-body img {
  max-width: 100%;
  cursor: auto !important;
}
</style>